import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Plus, ArrowClockwise, Flask, ShieldCheck, X, CheckCircle,
  Warning, Clock, MagnifyingGlass, Sparkle,
} from '@phosphor-icons/react';
import { HALE_HAIR, HALE_TEXT_MUTED, HALE_TEXT_FAINT, PACKAGING_MATERIALS } from './_design';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  intake:          { color: '#9CA3AF', label: 'Documentation intake' },
  in_review:       { color: '#F59E0B', label: 'In governance review' },
  reviewed_pass:   { color: '#10B981', label: 'Reviewed · cleared' },
  further_review:  { color: '#F59E0B', label: 'Escalation review recommended' },
  concern:         { color: '#E63946', label: 'Escalation review recommended' },
  certified:       { color: '#10B981', label: 'Governance Infrastructure Active' },
  rejected:        { color: '#9B1C1C', label: 'Not activated' },
  retired:         { color: '#6B7280', label: 'Retired' },
};

const VERDICT_TONE = {
  no_concern:            { color: '#10B981', dot: 'No significant concern' },
  monitor:               { color: '#F59E0B', dot: 'Monitor' },
  further_review:        { color: '#3B82F6', dot: 'Further review recommended' },
  high_priority_concern: { color: '#E63946', dot: 'Escalation review recommended' },
};

const CATEGORIES = [
  { key: 'face_cream',   label: 'Face cream' },
  { key: 'serum',        label: 'Serum / treatment' },
  { key: 'cleanser',     label: 'Cleanser' },
  { key: 'fragrance',    label: 'Fragrance' },
  { key: 'haircare',     label: 'Haircare' },
  { key: 'supplement',   label: 'Supplement' },
  { key: 'body_care',    label: 'Body care' },
  { key: 'deodorant',    label: 'Deodorant / antiperspirant' },
  { key: 'sunscreen',    label: 'Sunscreen' },
  { key: 'lipcare',      label: 'Lip care' },
  { key: 'other',        label: 'Other' },
];

export default function PortfolioSection({ membership }) {
  const [usage, setUsage] = useState(null);
  const [skus, setSkus] = useState([]);
  const [invoiceRequest, setInvoiceRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);

  const isActive = membership?.status === 'active';
  const isPendingPayment = membership?.status === 'pending_payment';
  const isAwaitingInvoice = membership?.status === 'awaiting_invoice';

  const reload = useCallback(async () => {
    try {
      const [u, s, ir] = await Promise.all([
        axios.get(`${API}/api/hale-marketplace/memberships/me/usage`, { withCredentials: true }),
        axios.get(`${API}/api/hale-marketplace/skus/me`, { withCredentials: true }),
        axios.get(`${API}/api/hale-marketplace/memberships/me/invoice-request`, { withCredentials: true })
          .catch(() => ({ data: { invoice_request: null } })),
      ]);
      setUsage(u.data); setSkus(s.data?.items || []);
      setInvoiceRequest(ir.data?.invoice_request || null);
    } catch (e) {
      // Membership may not exist yet — surface a stale-friendly empty state.
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || 'Could not load your portfolio');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const runScreen = async (id) => {
    try {
      const r = await axios.post(`${API}/api/hale-marketplace/skus/me/${id}/screen`, {},
        { withCredentials: true });
      toast.success(`Governance review complete: ${r.data?.verdict?.label || '—'}`);
      await reload();
      if (selectedSku?.id === id) {
        const fresh = await axios.get(`${API}/api/hale-marketplace/skus/me/${id}`,
          { withCredentials: true });
        setSelectedSku(fresh.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Screen failed');
    }
  };

  if (loading) {
    return <div className="mt-12 text-[#0B1426]/40 text-sm">Loading portfolio…</div>;
  }
  if (!usage) return null;

  return (
    <section className="mt-16" data-testid="portfolio-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946]">
            Retailer readiness · governance infrastructure
          </p>
          <h2 className="text-3xl mt-2 font-light"
              style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}>
            Your governance portfolio.
          </h2>
        </div>
        {isActive && (
          <Button
            data-testid="open-sku-intake"
            onClick={() => setIntakeOpen(true)}
            className="rounded-full bg-[#E63946] text-black h-11 px-6 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#C9192C]"
          >
            <Plus size={14} weight="bold" className="mr-2" /> Add SKU
          </Button>
        )}
      </div>

      <UsageCard
        usage={usage}
        isActive={isActive}
        isPendingPayment={isPendingPayment}
        isAwaitingInvoice={isAwaitingInvoice}
        invoiceRequest={invoiceRequest}
        onRequestInvoice={() => setInvoiceOpen(true)}
      />

      {isActive && (
        <SKUTable skus={skus} onOpen={setSelectedSku} onScreen={runScreen} />
      )}

      {selectedSku && (
        <SKUDrawer sku={selectedSku} onClose={() => setSelectedSku(null)} onScreen={runScreen} />
      )}
      {intakeOpen && (
        <IntakeDialog
          onClose={() => setIntakeOpen(false)}
          onCreated={async () => { setIntakeOpen(false); await reload(); }}
        />
      )}
      {invoiceOpen && (
        <InvoiceRequestDialog
          membership={membership}
          existing={invoiceRequest}
          onClose={() => setInvoiceOpen(false)}
          onSubmitted={async () => { setInvoiceOpen(false); await reload(); }}
        />
      )}
    </section>
  );
}

function UsageCard({ usage, isActive, isPendingPayment, isAwaitingInvoice,
                    invoiceRequest, onRequestInvoice }) {
  const pct = usage.sku_cap > 0 ? Math.min(100, Math.round((usage.sku_used / usage.sku_cap) * 100)) : 0;
  return (
    <div className="rounded-lg p-7 mb-10"
         style={{ backgroundColor: 'rgba(11,20,38,0.03)', border: `1px solid ${HALE_HAIR}` }}
         data-testid="usage-card">
      <div className="grid lg:grid-cols-4 gap-8">
        <Stat label="Membership" value={usage.tier_label || '—'} />
        <Stat label="Governance coverage" value={`${usage.sku_used} / ${usage.sku_cap} SKUs`} />
        <Stat label="Badges earned" value={usage.badges_earned ?? 0} />
        <Stat label="Renews" value={usage.renews_at ? new Date(usage.renews_at).toLocaleDateString() : '—'} />
      </div>

      {/* Capacity bar */}
      {isActive && (
        <div className="mt-7">
          <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(11,20,38,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-500"
                 style={{ width: `${pct}%`, backgroundColor: pct >= 90 ? '#E63946' : '#D9C5A7' }} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40 mt-2">
            {pct}% of governance capacity engaged
          </p>
        </div>
      )}

      {/* Pending-payment → invoice request CTA */}
      {isPendingPayment && (
        <div className="mt-7 pt-7 border-t" style={{ borderColor: HALE_HAIR }}>
          <p className="text-[15px] text-[#0B1426]/85 leading-relaxed">
            Your application has been approved. Activate your membership by requesting an invoice
            from our finance team — payable by ACH, wire, bill.com, or check.
          </p>
          <p className="text-xs text-[#0B1426]/40 mt-3">
            Enterprise governance memberships are contract-based and invoiced. We work with your
            AP team to keep procurement simple.
          </p>
          <Button
            data-testid="request-invoice"
            onClick={onRequestInvoice}
            className="mt-5 rounded-full bg-[#E63946] text-black h-11 px-7 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#C9192C]"
          >
            Request invoice →
          </Button>
        </div>
      )}

      {/* Awaiting-invoice state — request has been submitted */}
      {isAwaitingInvoice && invoiceRequest && (
        <div className="mt-7 pt-7 border-t" style={{ borderColor: HALE_HAIR }} data-testid="invoice-status">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D9C5A7] mb-2">
            Invoice in flight · {invoiceRequest.status.replace('_', ' ')}
          </p>
          <p className="text-[15px] text-[#0B1426]/85 leading-relaxed">
            Our finance team has received your request for <strong>{invoiceRequest.billing_entity}</strong>
            {' '}via <strong>{invoiceRequest.payment_method.replace('_', '.')}</strong>.
            You&apos;ll receive the invoice at <strong>{invoiceRequest.ap_contact_email}</strong> within
            one business day. Membership activates automatically once funds clear.
          </p>
          <button
            data-testid="update-invoice-request"
            onClick={onRequestInvoice}
            className="text-xs text-[#0B1426]/50 hover:text-[#E63946] mt-4 uppercase tracking-[0.18em] font-bold"
          >
            Update billing details →
          </button>
        </div>
      )}
    </div>
  );
}

function InvoiceRequestDialog({ membership, existing, onClose, onSubmitted }) {
  const [billingEntity, setBillingEntity] = useState(existing?.billing_entity || membership?.brand_name || '');
  const [apName, setApName] = useState(existing?.ap_contact_name || '');
  const [apEmail, setApEmail] = useState(existing?.ap_contact_email || '');
  const [billingAddress, setBillingAddress] = useState(existing?.billing_address || '');
  const [method, setMethod] = useState(existing?.payment_method || 'ach');
  const [poRequired, setPoRequired] = useState(existing?.purchase_order_required || false);
  const [notes, setNotes] = useState(existing?.notes || '');
  const [submitting, setSubmitting] = useState(false);

  const METHODS = [
    { key: 'ach',     label: 'ACH', sub: 'US bank transfer' },
    { key: 'wire',    label: 'Wire', sub: 'Domestic or international' },
    { key: 'bill_com', label: 'bill.com', sub: 'Sent via your bill.com portal' },
    { key: 'check',   label: 'Check', sub: 'Mailed to billing address' },
  ];

  const submit = async () => {
    if (!billingEntity.trim()) { toast.error('Billing entity is required'); return; }
    if (!apName.trim()) { toast.error('AP contact name is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(apEmail)) { toast.error('AP contact email is invalid'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/hale-marketplace/memberships/me/invoice-request`, {
        billing_entity: billingEntity,
        ap_contact_name: apName,
        ap_contact_email: apEmail,
        billing_address: billingAddress || null,
        payment_method: method,
        purchase_order_required: poRequired,
        notes: notes || null,
      }, { withCredentials: true });
      toast.success('Invoice request submitted. Finance will be in touch within one business day.');
      await onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit invoice request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="invoice-dialog">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <div className="w-full max-w-2xl bg-[#0A0A0A] border-l overflow-y-auto"
           style={{ borderColor: HALE_HAIR }}
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-7 py-5 border-b sticky top-0 bg-[#0A0A0A]"
                style={{ borderColor: HALE_HAIR }}>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946]">Phase 1 · invoiced activation</p>
            <h3 className="text-xl text-[#0B1426] mt-1">Request your membership invoice</h3>
          </div>
          <button onClick={onClose} className="text-[#0B1426]/40 hover:text-[#0B1426]"
                  data-testid="close-invoice">
            <X size={20} />
          </button>
        </header>

        <div className="px-7 py-7 space-y-7">
          <div className="rounded-md p-4"
               style={{ backgroundColor: 'rgba(217,197,167,0.06)', border: '1px solid rgba(217,197,167,0.25)' }}>
            <p className="text-[12px] text-[#D9C5A7] leading-relaxed">
              Enterprise memberships are contract-based and invoiced. Submit your billing details
              and our finance team will send the invoice within one business day.
            </p>
          </div>

          <Field label="Legal billing entity">
            <input
              data-testid="invoice-billing-entity"
              value={billingEntity} onChange={(e) => setBillingEntity(e.target.value)}
              placeholder="e.g. Acme Brands, LLC"
              className="w-full bg-transparent border-b text-base text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
              style={{ borderColor: HALE_HAIR }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-7">
            <Field label="AP contact name">
              <input
                data-testid="invoice-ap-name"
                value={apName} onChange={(e) => setApName(e.target.value)}
                placeholder="Accounts Payable lead"
                className="w-full bg-transparent border-b text-base text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
                style={{ borderColor: HALE_HAIR }}
              />
            </Field>
            <Field label="AP contact email">
              <input
                data-testid="invoice-ap-email"
                value={apEmail} onChange={(e) => setApEmail(e.target.value)}
                type="email" placeholder="ap@yourbrand.com"
                className="w-full bg-transparent border-b text-base text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
                style={{ borderColor: HALE_HAIR }}
              />
            </Field>
          </div>

          <Field label="Billing address (optional)">
            <textarea
              data-testid="invoice-billing-address"
              value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)}
              rows={2} placeholder="Street, City, State, ZIP"
              className="w-full bg-transparent border-b text-sm text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
              style={{ borderColor: HALE_HAIR }}
            />
          </Field>

          <Field label="Preferred payment method">
            <div className="grid grid-cols-2 gap-2 mt-1">
              {METHODS.map((m) => (
                <button
                  key={m.key}
                  data-testid={`invoice-method-${m.key}`}
                  onClick={() => setMethod(m.key)}
                  className={`text-left p-3 rounded-md border transition-colors ${
                    method === m.key
                      ? 'border-[#E63946] bg-[#E63946]/10'
                      : 'border-[#0B1426]/10 bg-white/[0.02] hover:border-[#0B1426]/30'
                  }`}
                >
                  <p className="text-sm font-bold text-[#0B1426]">{m.label}</p>
                  <p className="text-[11px] text-[#0B1426]/50 mt-0.5">{m.sub}</p>
                </button>
              ))}
            </div>
          </Field>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="invoice-po-required"
              type="checkbox"
              checked={poRequired}
              onChange={(e) => setPoRequired(e.target.checked)}
              className="rounded border-[#0B1426]/30 bg-transparent"
            />
            <span className="text-sm text-[#0B1426]/80">Purchase order required before invoice</span>
          </label>

          <Field label="Notes for finance team (optional)">
            <textarea
              data-testid="invoice-notes"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3} placeholder="PO #, contract reference, special terms…"
              className="w-full bg-transparent border-b text-sm text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
              style={{ borderColor: HALE_HAIR }}
            />
          </Field>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: HALE_HAIR }}>
            <Button variant="outline" onClick={onClose}
                    className="rounded-full bg-transparent border-[#0B1426]/15 text-[#0B1426]/70 text-[11px] uppercase tracking-[0.22em] font-bold h-10 px-5">
              Cancel
            </Button>
            <Button
              data-testid="invoice-submit"
              onClick={submit} disabled={submitting}
              className="rounded-full bg-[#E63946] text-black hover:bg-[#C9192C] text-[11px] uppercase tracking-[0.22em] font-bold h-10 px-6"
            >
              {submitting ? 'Submitting…' : 'Submit invoice request →'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#0B1426]/40 mb-2">{label}</p>
      <p className="text-2xl font-light text-[#0B1426]">{value || '—'}</p>
    </div>
  );
}

function SKUTable({ skus, onOpen, onScreen }) {
  if (!skus.length) {
    return (
      <div className="rounded-lg p-12 text-center"
           style={{ backgroundColor: 'rgba(11,20,38,0.02)', border: `1px dashed ${HALE_HAIR}` }}
           data-testid="empty-skus">
        <Sparkle size={32} weight="duotone" className="mx-auto text-[#0B1426]/30" />
        <p className="mt-5 text-base text-[#0B1426]/70">No SKUs in your governance portfolio yet.</p>
        <p className="text-sm text-[#0B1426]/40 mt-2">
          Add your first product to begin the documentation review pipeline.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${HALE_HAIR}` }}>
      <table className="w-full text-sm" data-testid="sku-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40"
              style={{ backgroundColor: 'rgba(11,20,38,0.03)' }}>
            <th className="px-5 py-3 font-bold">Product</th>
            <th className="px-5 py-3 font-bold">Category</th>
            <th className="px-5 py-3 font-bold">Readiness status</th>
            <th className="px-5 py-3 font-bold">Governance signal</th>
            <th className="px-5 py-3 font-bold">Last activity</th>
            <th className="px-5 py-3 font-bold text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {skus.map((s) => {
            const status = STATUS_TONE[s.status] || STATUS_TONE.intake;
            const v = s.verdict?.key ? VERDICT_TONE[s.verdict.key] : null;
            return (
              <tr key={s.id} className="border-t" style={{ borderColor: HALE_HAIR }}
                  data-testid={`sku-row-${s.id}`}>
                <td className="px-5 py-4">
                  <button onClick={() => onOpen(s)} className="text-[#0B1426] hover:underline text-left">
                    {s.product_name}
                  </button>
                  <p className="text-[11px] text-[#0B1426]/40 mt-0.5">
                    {s.ingredients_count} ingredients · {s.leave_on ? 'leave-on' : 'rinse-off'}
                  </p>
                </td>
                <td className="px-5 py-4 text-[#0B1426]/70">{s.product_category}</td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em]"
                        style={{ color: status.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status.color }} />
                    {status.label}
                  </span>
                </td>
                <td className="px-5 py-4">
                  {v ? (
                    <span className="text-[12px] font-bold" style={{ color: v.color }}>
                      {v.dot}
                    </span>
                  ) : <span className="text-[#0B1426]/30 text-xs">—</span>}
                </td>
                <td className="px-5 py-4 text-[#0B1426]/50 text-xs">
                  {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-5 py-4 text-right">
                  <Button
                    data-testid={`run-screen-${s.id}`}
                    onClick={() => onScreen(s.id)}
                    size="sm"
                    variant="outline"
                    className="rounded-full bg-transparent border-[#0B1426]/15 text-[#0B1426]/80 hover:border-[#E63946] hover:text-[#E63946] text-[10px] uppercase tracking-[0.18em] font-bold h-8 px-4"
                  >
                    <Flask size={12} weight="fill" className="mr-1.5" /> Run governance review
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SKUDrawer({ sku, onClose, onScreen }) {
  const [detail, setDetail] = useState(null);
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/hale-marketplace/skus/me/${sku.id}`,
          { withCredentials: true });
        if (alive) setDetail(r.data);
        try {
          const b = await axios.get(`${API}/api/hale-marketplace/skus/me/${sku.id}/badge`,
            { withCredentials: true });
          if (alive) setBadge(b.data);
        } catch (badgeErr) {
          // Badge not yet finalised by reviewer — expected pre-issuance state.
          if (badgeErr?.response?.status !== 409) {
            console.warn('Badge fetch failed:', badgeErr);
          }
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [sku.id]);

  const v = detail?.verdict;
  const tone = v?.key ? VERDICT_TONE[v.key] : null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="sku-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <div className="w-full max-w-xl bg-[#0A0A0A] border-l overflow-y-auto"
           style={{ borderColor: HALE_HAIR }}
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-7 py-5 border-b sticky top-0 bg-[#0A0A0A]"
                style={{ borderColor: HALE_HAIR }}>
          <div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#0B1426]/40">SKU detail</p>
            <h3 className="text-xl text-[#0B1426] mt-1">{sku.product_name}</h3>
          </div>
          <button onClick={onClose} className="text-[#0B1426]/40 hover:text-[#0B1426]" data-testid="close-sku-drawer">
            <X size={20} />
          </button>
        </header>

        <div className="px-7 py-7">
          {loading ? (
            <p className="text-[#0B1426]/40 text-sm">Loading…</p>
          ) : (
            <>
              {/* Verdict */}
              {v?.key ? (
                <div className="rounded-lg p-6 mb-7"
                     style={{ backgroundColor: 'rgba(11,20,38,0.03)', border: `1px solid ${tone?.color || HALE_HAIR}` }}>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40 mb-2">
                    Governance signal
                  </p>
                  <p className="text-xl font-bold" style={{ color: tone?.color }}>{v.label}</p>
                  {detail.verdict_detail?.summary && (
                    <p className="text-sm text-[#0B1426]/75 mt-4 leading-relaxed">
                      {detail.verdict_detail.summary}
                    </p>
                  )}
                  {detail.verdict_detail?.recommended_next_steps?.length > 0 && (
                    <div className="mt-5">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40 mb-2">Recommended actions</p>
                      <ul className="space-y-1.5">
                        {detail.verdict_detail.recommended_next_steps.map((n) => (
                          <li key={n} className="text-sm text-[#0B1426]/75">— {n}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg p-6 mb-7 text-center"
                     style={{ backgroundColor: 'rgba(11,20,38,0.02)', border: `1px dashed ${HALE_HAIR}` }}>
                  <Clock size={28} className="mx-auto text-[#0B1426]/30" />
                  <p className="text-sm text-[#0B1426]/60 mt-3">No governance review yet. Run the review to populate documentation status.</p>
                  <Button
                    data-testid="drawer-run-screen"
                    onClick={() => onScreen(sku.id)}
                    className="mt-4 rounded-full bg-[#E63946] text-black h-9 px-5 text-[10px] uppercase tracking-[0.22em] font-bold"
                  >
                    Run governance review
                  </Button>
                </div>
              )}

              {/* Badge */}
              {badge?.verdict_key && (
                <div className="rounded-lg p-6 mb-7"
                     style={{ backgroundColor: 'rgba(217,197,167,0.08)', border: '1px solid rgba(217,197,167,0.35)' }}
                     data-testid="badge-issued">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D9C5A7] mb-2">Governance infrastructure active</p>
                  <p className="text-base text-[#0B1426]">{badge.verdict_label}</p>
                  <p className="text-xs text-[#0B1426]/50 mt-2">
                    Finalised {new Date(badge.finalised_at).toLocaleDateString()}
                  </p>
                </div>
              )}

              {/* Composition */}
              <div className="mb-7">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40 mb-3">
                  Composition ({detail.ingredients?.length || 0})
                </p>
                <div className="rounded-md overflow-hidden" style={{ border: `1px solid ${HALE_HAIR}` }}>
                  <table className="w-full text-xs">
                    <tbody>
                      {(detail.ingredients || []).map((ing) => (
                        <tr key={ing.inci_name} className="border-t" style={{ borderColor: HALE_HAIR }}>
                          <td className="px-4 py-2 text-[#0B1426]/85">{ing.inci_name}</td>
                          <td className="px-4 py-2 text-[#0B1426]/50 text-right tabular-nums">
                            {Number(ing.concentration).toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {detail.packaging_materials?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40 mb-2">Packaging</p>
                  <p className="text-sm text-[#0B1426]/75">{detail.packaging_materials.join(' · ')}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function IntakeDialog({ onClose, onCreated }) {
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('face_cream');
  const [leaveOn, setLeaveOn] = useState(true);
  const [packaging, setPackaging] = useState([]);
  const [rows, setRows] = useState([{ _uid: 'r0', inci_name: '', concentration: '' }]);
  const [submitting, setSubmitting] = useState(false);

  const addRow = () => setRows([...rows, {
    _uid: `r${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    inci_name: '', concentration: '',
  }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, k, v) => {
    const next = rows.slice(); next[i] = { ...next[i], [k]: v }; setRows(next);
  };
  const togglePackaging = (m) =>
    setPackaging(packaging.includes(m) ? packaging.filter((x) => x !== m) : [...packaging, m]);

  const submit = async () => {
    const cleaned = rows
      .map((r) => ({ inci_name: r.inci_name.trim(), concentration: parseFloat(r.concentration) }))
      .filter((r) => r.inci_name && !Number.isNaN(r.concentration));
    if (!productName.trim()) { toast.error('Product name is required'); return; }
    if (cleaned.length === 0) { toast.error('At least one ingredient is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/hale-marketplace/skus`, {
        product_name: productName,
        product_category: category,
        leave_on: leaveOn,
        ingredients: cleaned,
        packaging_materials: packaging,
      }, { withCredentials: true });
      toast.success('SKU added to your portfolio.');
      await onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add SKU');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="intake-dialog">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <div className="w-full max-w-2xl bg-[#0A0A0A] border-l overflow-y-auto"
           style={{ borderColor: HALE_HAIR }}
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-7 py-5 border-b sticky top-0 bg-[#0A0A0A]"
                style={{ borderColor: HALE_HAIR }}>
          <div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#E63946]">Add to governance portfolio</p>
            <h3 className="text-xl text-[#0B1426] mt-1">New SKU intake</h3>
          </div>
          <button onClick={onClose} className="text-[#0B1426]/40 hover:text-[#0B1426]"
                  data-testid="close-intake">
            <X size={20} />
          </button>
        </header>

        <div className="px-7 py-7 space-y-7">
          <Field label="Product name">
            <input
              data-testid="intake-product-name"
              value={productName} onChange={(e) => setProductName(e.target.value)}
              className="w-full bg-transparent border-b text-base text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
              style={{ borderColor: HALE_HAIR }}
              placeholder="e.g. Hydration Recovery Serum"
            />
          </Field>

          <div className="grid grid-cols-2 gap-7">
            <Field label="Category">
              <select
                data-testid="intake-category"
                value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-transparent border-b text-base text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
                style={{ borderColor: HALE_HAIR }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key} className="bg-[#0A0A0A]">{c.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Application">
              <div className="flex gap-2 mt-1">
                <Pill data-testid="intake-leaveon" active={leaveOn} onClick={() => setLeaveOn(true)}>Leave-on</Pill>
                <Pill data-testid="intake-rinseoff" active={!leaveOn} onClick={() => setLeaveOn(false)}>Rinse-off</Pill>
              </div>
            </Field>
          </div>

          <Field label="Packaging (optional)">
            <div className="flex flex-wrap gap-2">
              {PACKAGING_MATERIALS.map((m) => (
                <Pill key={m} active={packaging.includes(m)} onClick={() => togglePackaging(m)}>
                  {m}
                </Pill>
              ))}
            </div>
          </Field>

          <Field label="Composition">
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={r._uid} className="flex gap-2 items-center">
                  <input
                    data-testid={`intake-inci-${i}`}
                    value={r.inci_name}
                    onChange={(e) => updateRow(i, 'inci_name', e.target.value)}
                    placeholder="INCI name"
                    className="flex-1 bg-transparent border-b text-sm text-[#0B1426] py-2 focus:outline-none focus:border-[#E63946]"
                    style={{ borderColor: HALE_HAIR }}
                  />
                  <input
                    data-testid={`intake-conc-${i}`}
                    value={r.concentration}
                    onChange={(e) => updateRow(i, 'concentration', e.target.value)}
                    placeholder="%"
                    type="number" step="0.001" min="0" max="100"
                    className="w-24 bg-transparent border-b text-sm text-[#0B1426] py-2 text-right tabular-nums focus:outline-none focus:border-[#E63946]"
                    style={{ borderColor: HALE_HAIR }}
                  />
                  {rows.length > 1 && (
                    <button onClick={() => removeRow(i)} className="text-[#0B1426]/30 hover:text-[#E63946]"
                            data-testid={`intake-remove-${i}`}>
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addRow} className="text-xs text-[#E63946] hover:text-[#C9192C] flex items-center gap-1 mt-2"
                      data-testid="intake-add-row">
                <Plus size={12} weight="bold" /> Add ingredient
              </button>
            </div>
          </Field>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: HALE_HAIR }}>
            <Button variant="outline" onClick={onClose}
                    className="rounded-full bg-transparent border-[#0B1426]/15 text-[#0B1426]/70 text-[11px] uppercase tracking-[0.22em] font-bold h-10 px-5">
              Cancel
            </Button>
            <Button
              data-testid="intake-submit"
              onClick={submit} disabled={submitting}
              className="rounded-full bg-[#E63946] text-black hover:bg-[#C9192C] text-[11px] uppercase tracking-[0.22em] font-bold h-10 px-6"
            >
              {submitting ? 'Adding…' : 'Add to governance portfolio →'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#0B1426]/40 mb-2">{label}</p>
      {children}
    </div>
  );
}

function Pill({ active, children, onClick, ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors
        ${active
          ? 'bg-[#E63946] text-black border-[#E63946]'
          : 'bg-transparent text-[#0B1426]/65 border-[#0B1426]/15 hover:border-[#0B1426]/40'}`}
      {...rest}
    >
      {children}
    </button>
  );
}
